// El carrito del microsite frente al menú FRESCO.
//
// La carta se lee una vez al cargar la página (en producción, además, desde un
// cache por slug) y el carrito vive en el navegador. Si el dueño agota un plato o
// un ingrediente mientras el cliente arma su pedido, el carrito lo sigue
// ofreciendo, y el cliente se enteraba recién en el pago: /session respondía 409
// y "Reintentar" mandaba el mismo carrito. Esto decide qué líneas ya no se pueden
// vender, para quitarlas y avisar ANTES de cobrar.
//
// Platos e ingredientes usan el criterio del kiosko (lib/kiosk/cartReconcile): una
// sola definición de "esto ya no se vende", con la misma excepción para lo que se
// QUITA. Encima va la ventana horaria, que el kiosko no necesita porque su menú se
// refresca solo.
//
// Es una AYUDA, no la puerta: /session vuelve a validar todo antes de cobrar.

import { reconcileCart } from '@/lib/kiosk/cartReconcile'
import { isItemAvailableNow } from '@/lib/menu/availability'
import type { CartItem, SoldOutNotice } from '@/components/microsite/types'
import { micrositeLineName, type MicrositeLocale } from '@/lib/microsite/couponUx'
import { productCartLine } from '@/lib/storefront/productCartLine'
import type { StorefrontRetailRow } from '@/lib/storefront/retailRow'
import type { CartLineFacts } from '@/lib/storefront/groceryCart'
import type { PricingConfig } from '@/lib/utils/pricing'

export type CartAvailabilityOption = {
  id: string
  name?: string | null
  price_delta?: number | null
  is_available?: boolean | null
}

/** Un plato tal como lo devuelve /api/online-orders/cart-availability. */
export type CartAvailabilityItem = {
  id: string
  name?: string | null
  is_available: boolean | null
  is_active?: boolean | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
  item_modifier_groups?: ReadonlyArray<{
    modifier_groups?: {
      display_style?: string | null
      modifier_options?: ReadonlyArray<CartAvailabilityOption> | null
    } | null
  }> | null
  // ── Súper online (PR 4, D10): precio fresco. La ruta los manda a todo local; solo los
  //    lee `refreshRetailLinePrices`, y solo con `online_retail_v2`. Opcionales: una
  //    respuesta sin ellos (servidor anterior) no refresca nada.
  /** `items.price`: precio de tarjeta CRUDO (la línea simple le aplica el efectivo). */
  price?: number | null
  is_taxable?: boolean | null
  is_retail?: boolean | null
  pricing_mode?: string | null
  has_variants?: boolean | null
  weight_unit?: string | null
  item_variants?: ReadonlyArray<CartAvailabilityVariant> | null
}

/** Variante tal como la devuelve cart-availability (D10). */
export type CartAvailabilityVariant = {
  id: string
  name: string
  price: number | null
  is_active: boolean | null
  sort_order: number | null
}

export type CartAvailabilitySnapshot = {
  /** Zona horaria del local, para la ventana de venta. */
  timezone: string | null
  items: CartAvailabilityItem[]
}

/**
 * Las líneas del carrito que el menú fresco ya no deja vender, en el orden del
 * carrito, con el nombre para el aviso: "Plato" si se agotó, se ocultó o salió de
 * su horario; "Plato (Ingrediente)" si fue un ingrediente agregado.
 *
 * Súper online (minor 5 de la revisión final): el nombre es el de la línea en el
 * carrito (`micrositeLineName`): «Leche · Galón», «Banana · 1,5 lb». Una línea de
 * restaurante (sin peso ni variante) devuelve `name` tal cual: el aviso de main.
 */
export function unsellableCartLines(
  cart: ReadonlyArray<
    Pick<CartItem, 'cartId' | 'itemId' | 'name' | 'modifiers' | 'weightMilli' | 'weightUnit' | 'variantId' | 'variantName'>
  >,
  snapshot: CartAvailabilitySnapshot,
  now: Date = new Date(),
  locale: MicrositeLocale = 'es',
): Array<{ cartId: string; name: string }> {
  // Oculto del menú (`is_active=false`) cuenta igual que borrado: ya no está en la carta.
  const visibles = snapshot.items.filter((i) => i.is_active !== false)
  const lineName = (l: (typeof cart)[number]) => micrositeLineName(l, locale)
  const { removed } = reconcileCart(
    cart.map((l) => ({ cart_id: l.cartId, item_id: l.itemId, name: lineName(l), modifiers: l.modifiers })),
    visibles,
  )
  const motivo = new Map(removed.map((r) => [r.cart_id, r.name]))

  const tz = snapshot.timezone ?? 'UTC'
  const porId = new Map(visibles.map((i) => [i.id, i]))
  for (const linea of cart) {
    if (motivo.has(linea.cartId)) continue
    const item = porId.get(linea.itemId)
    if (item && !isItemAvailableNow(item, tz, now)) motivo.set(linea.cartId, lineName(linea))
  }

  return cart.flatMap((l) => {
    const name = motivo.get(l.cartId)
    return name ? [{ cartId: l.cartId, name }] : []
  })
}

// ── Súper online PR 4 (Tarea 7b, D10): precio fresco al abrir el carrito ──────────────

/**
 * La fila fresca de cart-availability con la forma que espera `productCartLine` (la de
 * `toStorefrontRows`): variantes activas en `sort_order`, sin modificadores. Lo que la
 * ruta no manda (foto, descripción, contenido neto) va en null: la línea del carrito no
 * lo usa. `weight_unit` viaja SIEMPRE como clave (null si no hay): es lo que
 * `isRetailWeightItem` mira para reconocer un producto por peso.
 */
export function freshProductRow(item: CartAvailabilityItem): StorefrontRetailRow {
  const variants = (item.item_variants ?? [])
    .filter((v) => v.is_active !== false)
    .map((v) => ({ ...v, net_content: null, net_content_unit: null }))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  return {
    id: item.id,
    name: item.name ?? '',
    description: null,
    translations: null,
    price: item.price ?? 0,
    image_url: null,
    image_focal: null,
    is_available: item.is_available,
    category_id: null,
    sort_order: null,
    available_from: item.available_from ?? null,
    available_until: item.available_until ?? null,
    available_days: item.available_days ?? null,
    is_retail: item.is_retail ?? null,
    pricing_mode: item.pricing_mode ?? null,
    has_variants: item.has_variants ?? null,
    weight_unit: item.weight_unit ?? null,
    is_taxable: item.is_taxable ?? null,
    net_content: null,
    net_content_unit: null,
    is_alcohol: null,
    is_tobacco: null,
    variants,
    item_modifier_groups: [],
    item_default_options: [],
  }
}

/** Línea retail del súper: por peso, con variante, o de un producto `is_retail`. */
function isRetailLine(line: CartItem, fresh: CartAvailabilityItem): boolean {
  return line.weightMilli != null || line.variantId != null || fresh.is_retail === true
}

/**
 * ¿Dos líneas son la misma línea (mismas claves con los mismos valores)? Una línea retail
 * nunca lleva modificadores: dos listas vacías cuentan como iguales.
 */
function sameLine(a: CartItem, b: CartItem): boolean {
  const ka = Object.keys(a) as Array<keyof CartItem>
  const kb = Object.keys(b) as Array<keyof CartItem>
  // `isRetail` es una marca solo-cliente: no cuenta como cambio de la línea.
  const sinMarca = (k: keyof CartItem) => k !== 'isRetail'
  const ka2 = ka.filter(sinMarca)
  const kb2 = kb.filter(sinMarca)
  const igual = (k: keyof CartItem) =>
    k === 'modifiers' ? a.modifiers === b.modifiers || (a.modifiers.length === 0 && b.modifiers.length === 0) : a[k] === b[k]
  return ka2.length === kb2.length && ka2.every(igual)
}

/**
 * Las líneas retail del carrito reconstruidas al precio de HOY (D10).
 *
 * Cada línea con peso, variante o de un producto `is_retail` se rearma con
 * `productCartLine` —la misma función que la agrega en el pasillo y el producto— sobre la
 * fila fresca, con su mismo `cartId`, `weightMilli` y `variantId`; de la línea vieja se
 * conservan cantidad, nota y preferencias de sustitución. Sin zona horaria: el refresco
 * cambia precios, no quita líneas (una fuera de horario la sigue rechazando el servidor).
 *
 * La línea queda COMO ESTABA (misma referencia) si:
 *   - no es retail (un plato de restaurante, también en un local mixto);
 *   - el producto no vino (borrado) o la respuesta no trae precio (servidor anterior);
 *   - `productCartLine` devuelve null (agotado, variante desactivada): la saca el paso de
 *     disponibilidad que ya existe (`unsellableCartLines`) o el servidor;
 *   - cambió la FORMA (por peso ↔ por unidad, la unidad de peso, la variante, o la línea
 *     lleva modificadores): reinterpretar «1500» como kg en vez de lb, o rearmar sin los
 *     extras, cambiaría lo que el cliente pidió; lo decide el servidor (sin dato fresco);
 *   - no cambió nada.
 *
 * `changed` nombra (como el carrito, `micrositeLineName`) las líneas cuyo precio cambió;
 * `facts` lleva por producto retail lo que el carrito guardado refresca: precio CRUDO,
 * `is_taxable` (lo ausente = gravable, como `factsOf`) e `is_available`.
 */
export function refreshRetailLinePrices(
  cart: CartItem[],
  fresh: readonly CartAvailabilityItem[],
  cfg: PricingConfig,
  locale: MicrositeLocale = 'es',
): { cart: CartItem[]; changed: string[]; changedCartIds: string[]; facts: Record<string, Partial<CartLineFacts>> } {
  const porId = new Map(fresh.map((i) => [i.id, i]))
  const changed: string[] = []
  const changedCartIds: string[] = []
  const facts: Record<string, Partial<CartLineFacts>> = {}

  const next = cart.map((old) => {
    const item = porId.get(old.itemId)
    if (!item || !isRetailLine(old, item) || typeof item.price !== 'number') return old
    // Con extras (un retail con grupos de modificadores sigue en la portada con la bandera):
    // rearmarla como línea simple los borraría en silencio. Es cambio de forma; el servidor
    // la rechaza como hoy (`retail_modifiers_not_supported`). Revisión 7b #2.
    if (old.modifiers.length > 0) return old
    facts[item.id] = {
      price: item.price,
      ...(item.is_taxable !== undefined ? { is_taxable: item.is_taxable !== false } : {}),
      is_available: item.is_available ?? null,
    }

    const rebuilt = productCartLine({
      item: freshProductRow(item),
      cfg,
      cartId: old.cartId,
      weightMilli: old.weightMilli,
      variantId: old.variantId ?? null,
    })
    if (!rebuilt) return old
    if (rebuilt.weightMilli !== old.weightMilli || rebuilt.weightUnit !== old.weightUnit || rebuilt.variantId !== old.variantId) {
      return old
    }

    const line: CartItem = {
      ...rebuilt,
      quantity: old.quantity,
      ...(old.specialInstructions !== undefined ? { specialInstructions: old.specialInstructions } : {}),
      ...(old.substitutionPref !== undefined ? { substitutionPref: old.substitutionPref } : {}),
      ...(old.substitutionItemId !== undefined ? { substitutionItemId: old.substitutionItemId } : {}),
      ...(old.isRetail !== undefined ? { isRetail: old.isRetail } : {}),
    }
    if (sameLine(line, old)) return old
    if (line.price !== old.price) {
      changed.push(micrositeLineName(line, locale))
      changedCartIds.push(line.cartId)
    }
    return line
  })

  return { cart: next, changed, changedCartIds, facts }
}

/**
 * Qué hace el re-chequeo del carrito con el menú fresco (lo que llama OrderSheet,
 * `recheckCartAvailability`, después de UN `requestCartAvailability`).
 *
 * Sin `retailV2` es EXACTAMENTE la decisión de main: `unsellableCartLines` sobre el carrito
 * tal cual; `cart` es el carrito sin lo que ya no se vende (o null si no hay nada que
 * quitar), `removed` sus nombres, y ni precio ni datos frescos
 * (`cartRecheckRestaurantRegression.test.ts` lo compara contra una copia congelada).
 *
 * Con `retailV2` (súper online, D10), antes de eso las líneas retail se reconstruyen al
 * precio de hoy (`refreshRetailLinePrices`); lo que además se quita no se anuncia como
 * cambio de precio. `cart` es null si ninguna línea cambió ni se quitó.
 */
export function planCartRecheck(
  cart: CartItem[],
  menu: CartAvailabilitySnapshot,
  opts: { retailV2: boolean; cfg: PricingConfig; locale: MicrositeLocale; now?: Date },
): {
  /** El carrito nuevo, o null: no hay que llamar `setCart`. */
  cart: CartItem[] | null
  /** Nombres de lo que se quitó (aviso «se agotó»). */
  removed: string[]
  /** Nombres de lo que cambió de precio (aviso de precio). Siempre vacío sin la bandera. */
  priceChanged: string[]
  /** Datos frescos por producto para el carrito guardado. null sin la bandera. */
  facts: Record<string, Partial<CartLineFacts>> | null
} {
  const { retailV2, cfg, locale, now } = opts
  if (!retailV2) {
    const quitar = unsellableCartLines(cart, menu, now, locale)
    if (quitar.length === 0) return { cart: null, removed: [], priceChanged: [], facts: null }
    const fuera = new Set(quitar.map((l) => l.cartId))
    return { cart: cart.filter((l) => !fuera.has(l.cartId)), removed: quitar.map((l) => l.name), priceChanged: [], facts: null }
  }

  const refreshed = refreshRetailLinePrices(cart, menu.items, cfg, locale)
  const quitar = unsellableCartLines(refreshed.cart, menu, now, locale)
  const fuera = new Set(quitar.map((l) => l.cartId))
  const kept = fuera.size === 0 ? refreshed.cart : refreshed.cart.filter((l) => !fuera.has(l.cartId))
  const priceChanged = refreshed.changed.filter((_, i) => !fuera.has(refreshed.changedCartIds[i]))
  const touched = fuera.size > 0 || refreshed.cart.some((l, i) => l !== cart[i])
  return {
    cart: touched ? kept : null,
    removed: quitar.map((l) => l.name),
    priceChanged,
    facts: refreshed.facts,
  }
}

/**
 * Aplica el plan del re-chequeo a la pantalla. Devuelve true si el carrito quedó igual
 * para el cliente (nada quitado, ningún precio cambiado): el checkout puede avanzar. Un
 * cambio solo de nombre se guarda en silencio y no frena.
 *
 * Sin la bandera son las llamadas de main, en su orden: `setCart` con lo que queda y el
 * aviso `removed`, o nada.
 */
export function applyCartRecheck(
  plan: ReturnType<typeof planCartRecheck>,
  sinks: {
    setCart: (cart: CartItem[]) => void
    setSoldOutNotice: (notice: SoldOutNotice | null) => void
    setPriceChangedNotice: (names: string[] | null) => void
    onFreshFacts?: (facts: Record<string, Partial<CartLineFacts>>) => void
  },
): boolean {
  if (plan.cart) {
    sinks.setCart(plan.cart)
    if (plan.removed.length > 0) sinks.setSoldOutNotice({ kind: 'removed', names: plan.removed })
    if (plan.priceChanged.length > 0) sinks.setPriceChangedNotice(plan.priceChanged)
  }
  if (plan.facts) sinks.onFreshFacts?.(plan.facts)
  return plan.removed.length === 0 && plan.priceChanged.length === 0
}

/**
 * Rechazos del súper online (Tarea 2b.3, R2), por status: stock que se acabó y
 * líneas retail que la tienda no puede vender así. Un restaurante nunca los recibe
 * (salen solo de productos `is_retail`). Vuelven al carrito con el `message` del
 * servidor, que nombra el producto: el menú fresco no los detecta.
 */
const RETAIL_REJECTIONS: Record<number, readonly string[]> = {
  409: ['out_of_stock'],
  422: [
    'item_not_sold_online',
    'retail_not_supported_online',
    'variant_required',
    'weight_required',
    'weight_not_allowed',
    'weight_invalid',
    'retail_modifiers_not_supported',
  ],
}

/** Errores de /session que significan "algo del carrito ya no se vende", por status. */
const AVAILABILITY_REJECTIONS: Record<number, readonly string[]> = {
  409: ['items_unavailable', 'modifiers_unavailable', ...RETAIL_REJECTIONS[409]],
  422: ['item_unavailable', ...RETAIL_REJECTIONS[422]],
}

/**
 * ¿Esta respuesta de /session es un rechazo por disponibilidad del menú? Llegan
 * ANTES de cobrar y se resuelven en el carrito, no reintentando el mismo pedido.
 * Los de envío y cupón tienen su propio camino; todo lo demás sigue siendo un
 * error de pago.
 */
export function isSessionAvailabilityRejection(status: number, body: unknown): boolean {
  const error = (body as { error?: unknown } | null)?.error
  return typeof error === 'string' && (AVAILABILITY_REJECTIONS[status] ?? []).includes(error)
}

/**
 * El mensaje en prosa de un rechazo RETAIL de /session (p. ej. «Se nos acabó:
 * Banana. Ajusta tu carrito.»), para mostrarlo en el carrito. null para cualquier
 * otro rechazo —los de restaurante siguen con su aviso de siempre— o sin mensaje.
 */
export function retailRejectionMessage(status: number, body: unknown): string | null {
  const b = body as { error?: unknown; message?: unknown } | null
  if (typeof b?.error !== 'string' || !(RETAIL_REJECTIONS[status] ?? []).includes(b.error)) return null
  return typeof b.message === 'string' && b.message.trim() ? b.message : null
}
